import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from "firebase/auth";
import { auth } from "../firebase";
import { resendVerificationEmail } from "../api/authApi";
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

function RoleToggle({ role, setRole, t }) {
  return (
    <div className="pt-1">
      <p className="mb-2 text-sm text-white/90">{t("auth.signup.iam")}</p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => setRole("student")}
          className={`rounded-xl border px-4 py-2 text-sm transition ${
            role === "student"
              ? "border-[#98dea3] bg-[#98dea3]/20 text-white"
              : "border-white/25 text-white/80 hover:text-white"
          }`}
          aria-pressed={role === "student"}
        >
          {t("roles.student")}
        </button>
        <button
          type="button"
          onClick={() => setRole("tutor")}
          className={`rounded-xl border px-4 py-2 text-sm transition ${
            role === "tutor"
              ? "border-[#98dea3] bg-[#98dea3]/20 text-white"
              : "border-white/25 text-white/80 hover:text-white"
          }`}
          aria-pressed={role === "tutor"}
        >
          {t("roles.tutor")}
        </button>
      </div>
    </div>
  );
}

export default function Signup() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const lang = (i18n.resolvedLanguage || i18n.language || "uk").startsWith("uk") ? "uk" : "en";

  // After auth, go back to the page that sent the user here (e.g. a tutor profile)
  const nextPath = new URLSearchParams(location.search).get("next") || null;

  // Pre-select role from URL param: /signup?role=tutor → specialist pre-selected
  const initialRole =
    new URLSearchParams(location.search).get("role") === "tutor" ? "tutor" : "student";

  // Tab: "email" | "phone"
  const [tab, setTab] = useState("email");

  // ── Email flow ──
  const [emailForm, setEmailForm] = useState({
    fullName: "",
    email: "",
    password: "",
    role: initialRole,
    showPassword: false,
  });
  const [firebaseUserRef, setFirebaseUserRef] = useState(null);
  const [pendingAuthData, setPendingAuthData] = useState(null); // held until verified
  const [verifyStep, setVerifyStep]     = useState(false);
  const [verifyTarget, setVerifyTarget] = useState("/dashboard");
  const [verifyError, setVerifyError]   = useState("");
  const [checkingVerified, setCheckingVerified] = useState(false);
  const [resent, setResent] = useState(false);

  // ── Phone flow ──
  const [phoneForm, setPhoneForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    role: initialRole,
  });
  const [otpStep, setOtpStep]                     = useState(false);
  const [otp, setOtp]                             = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [phoneLoading, setPhoneLoading]           = useState(false);
  const [phoneError, setPhoneError]               = useState("");
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef  = useRef(null);

  // Initialise invisible reCAPTCHA when switching to phone tab
  useEffect(() => {
    if (tab === "phone" && !recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          { size: "invisible" }
        );
      } catch (_) {}
    }
  }, [tab]);

  // ── Handlers: email ──
  const onEmailChange = (e) =>
    setEmailForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submitEmail = async (e) => {
    e.preventDefault();
    try {
      const userCred = await createUserWithEmailAndPassword(
        auth,
        emailForm.email,
        emailForm.password
      );
      const firebaseUser = userCred.user;
      const token = await firebaseUser.getIdToken(true);
      const roleForBackend = emailForm.role === "tutor" ? "TUTOR" : "STUDENT";

      const regRes = await fetch(`${API_URL}/api/auth/register-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: roleForBackend,
          name: emailForm.fullName,
          language: lang,
        }),
      });

      const regData = await regRes.json();
      if (!regRes.ok) {
        throw new Error(regData?.details || regData?.error || "register-profile failed");
      }

      // Do NOT write to localStorage yet — user hasn't verified their email.
      // Store the auth data in state; it will be committed after verification.
      // The backend sends our branded email via SMTP and now reports whether it
      // actually went out (verificationEmailSent). Only when it FAILED do we
      // fall back to Firebase's native verification email — it's unbranded
      // ("Verify your email for edora-dc48e") but guaranteed to be delivered,
      // so the user always gets at least one working link.
      // continueUrl redirects the user back to the app after they click the
      // verification link, instead of leaving them on edora-dc48e.firebaseapp.com.
      // Navbar's onAuthStateChanged will auto-fetch /api/auth/me on return.
      if (!regData.verificationEmailSent) {
        sendEmailVerification(firebaseUser, {
          url: `${window.location.origin}/dashboard`,
          handleCodeInApp: false,
        }).catch(() => {});
      }
      setPendingAuthData({
        id:    regData.user.id,
        email: regData.user.email,
        name:  regData.user.name,
        role:  regData.user.role,
        token,
      });

      setFirebaseUserRef(firebaseUser);
      const defaultTarget = regData.user.role === "TUTOR" ? "/onboarding/tutor" : "/dashboard";
      setVerifyTarget(nextPath || defaultTarget);
      setVerifyStep(true);
    } catch (err) {
      console.error("Signup error:", err);
      alert(`Registration failed: ${err.message}`);
    }
  };

  const handleCheckVerified = async () => {
    setCheckingVerified(true);
    setVerifyError("");
    try {
      await firebaseUserRef.reload();
      if (firebaseUserRef.emailVerified) {
        // Only now commit the auth data to localStorage
        if (pendingAuthData) {
          localStorage.setItem("auth", JSON.stringify(pendingAuthData));
          localStorage.setItem("isAuthed", "1");
        }
        navigate(verifyTarget);
      } else {
        setVerifyError(t("auth.verify.notYetError"));
      }
    } catch (err) {
      setVerifyError(err.message);
    } finally {
      setCheckingVerified(false);
    }
  };

  const handleResend = async () => {
    try {
      // Branded email via backend first; Firebase's unbranded default only
      // as a fallback when our SMTP is down.
      try {
        const token = pendingAuthData?.token || (await firebaseUserRef.getIdToken());
        await resendVerificationEmail(token, lang);
      } catch {
        await sendEmailVerification(firebaseUserRef);
      }
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (_) {}
  };

  // ── Handlers: phone ──
  const onPhoneChange = (e) =>
    setPhoneForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const sendOtp = async (e) => {
    e.preventDefault();
    setPhoneError("");
    setPhoneLoading(true);
    try {
      if (!recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          { size: "invisible" }
        );
      }
      const result = await signInWithPhoneNumber(
        auth,
        phoneForm.phone,
        recaptchaVerifierRef.current
      );
      setConfirmationResult(result);
      setOtpStep(true);
    } catch (err) {
      console.error("Phone OTP send error:", err);
      setPhoneError(t("auth.errors.otpSendFailed"));
      // Reset reCAPTCHA so user can retry
      try { recaptchaVerifierRef.current?.clear?.(); } catch (_) {}
      recaptchaVerifierRef.current = null;
    } finally {
      setPhoneLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setPhoneError("");
    setPhoneLoading(true);
    try {
      const userCred = await confirmationResult.confirm(otp);
      const firebaseUser = userCred.user;
      const token = await firebaseUser.getIdToken(true);
      const roleForBackend = phoneForm.role === "tutor" ? "TUTOR" : "STUDENT";

      const regRes = await fetch(`${API_URL}/api/auth/register-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role:     roleForBackend,
          name:     phoneForm.fullName,
          email:    phoneForm.email || null,
          language: lang,
        }),
      });

      const regData = await regRes.json();
      if (!regRes.ok) {
        throw new Error(regData?.details || regData?.error || "register-profile failed");
      }

      localStorage.setItem(
        "auth",
        JSON.stringify({
          id:    regData.user.id,
          email: regData.user.email,
          name:  regData.user.name,
          role:  regData.user.role,
          token,
        })
      );
      localStorage.setItem("isAuthed", "1");

      const defaultTarget = regData.user.role === "TUTOR" ? "/onboarding/tutor" : "/dashboard";
      navigate(nextPath || defaultTarget);
    } catch (err) {
      console.error("OTP verify error:", err);
      setPhoneError(t("auth.errors.otpWrong"));
    } finally {
      setPhoneLoading(false);
    }
  };

  // ── Email verification screen ──
  if (verifyStep) {
    return (
      <main className="min-h-screen bg-[#272c39] text-white">
        <div className="mx-auto max-w-3xl px-4 pt-10 pb-24">
          <div className="h-12" />
          <section className="mx-auto max-w-md text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#98dea3]/20 text-4xl">
              ✉️
            </div>
            <h1 className="font-unbounded text-2xl font-extrabold tracking-tight">
              {t("auth.verify.title")}
            </h1>
            <p className="mt-3 text-white/70">
              {t("auth.verify.body", { email: emailForm.email })}
            </p>
            <p className="mt-2 text-sm text-white/50">
              {t("auth.verify.spam")}
            </p>

            {verifyError && (
              <p className="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                {verifyError}
              </p>
            )}

            <button
              onClick={handleCheckVerified}
              disabled={checkingVerified}
              className="mt-8 w-full rounded-xl bg-[#7269e3] px-5 py-3 text-lg font-semibold text-white shadow-[0_6px_20px_rgba(114,105,227,0.35)] hover:brightness-110 disabled:opacity-60"
            >
              {checkingVerified ? t("auth.verify.checking") : t("auth.verify.checkBtn")}
            </button>

            <button
              type="button"
              onClick={handleResend}
              className="mt-4 text-sm text-white/50 hover:text-white/80"
            >
              {resent ? t("auth.verify.resent") : t("auth.verify.resend")}
            </button>
          </section>
        </div>
      </main>
    );
  }

  // ── Main signup form ──
  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-3xl px-4 pt-10 pb-24">
        <div className="h-12" />

        <section className="mx-auto max-w-md">
          <h1 className="text-center font-unbounded text-3xl font-extrabold tracking-tight">
            {t("auth.signup.title")}
          </h1>

          {/* Tab toggle */}
          <div className="mt-6 flex gap-1 rounded-xl border border-white/10 bg-white/5 p-1">
            <button
              type="button"
              onClick={() => setTab("email")}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${
                tab === "email"
                  ? "bg-[#7269e3] text-white shadow"
                  : "text-white/60 hover:text-white"
              }`}
            >
              {t("auth.tabs.email")}
            </button>
            <button
              type="button"
              onClick={() => setTab("phone")}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${
                tab === "phone"
                  ? "bg-[#7269e3] text-white shadow"
                  : "text-white/60 hover:text-white"
              }`}
            >
              {t("auth.tabs.phone")}
            </button>
          </div>

          {/* ── EMAIL FORM ── */}
          {tab === "email" && (
            <form onSubmit={submitEmail} className="mt-8 space-y-5">
              {/* Full name */}
              <div>
                <label htmlFor="fullName" className="mb-2 block text-sm text-white/90">
                  {t("auth.signup.fullName")}
                </label>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={emailForm.fullName}
                  onChange={onEmailChange}
                  placeholder={t("auth.signup.fullName_ph")}
                  className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 text-white placeholder-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="mb-2 block text-sm text-white/90">
                  {t("auth.signup.email")}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={emailForm.email}
                  onChange={onEmailChange}
                  placeholder={t("auth.signup.email_ph")}
                  className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 text-white placeholder-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="mb-2 block text-sm text-white/90">
                  {t("auth.signup.password")}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={emailForm.showPassword ? "text" : "password"}
                    value={emailForm.password}
                    onChange={onEmailChange}
                    placeholder={t("auth.signup.password_ph")}
                    className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 pr-12 text-white placeholder-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setEmailForm((f) => ({ ...f, showPassword: !f.showPassword }))
                    }
                    className="absolute inset-y-0 right-2 my-auto rounded-lg px-2 text-sm text-white/80 hover:text-white"
                    aria-label={
                      emailForm.showPassword
                        ? t("auth.signup.hidePassword")
                        : t("auth.signup.showPassword")
                    }
                  >
                    {emailForm.showPassword ? t("auth.signup.hide") : t("auth.signup.show")}
                  </button>
                </div>
              </div>

              <RoleToggle
                role={emailForm.role}
                setRole={(r) => setEmailForm((f) => ({ ...f, role: r }))}
                t={t}
              />

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full rounded-xl bg-[#7269e3] px-5 py-3 text-lg font-semibold text-white shadow-[0_6px_20px_rgba(114,105,227,0.35)] hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#a783a6]"
                >
                  {t("auth.signup.submit")}
                </button>
              </div>

              <p className="text-center text-white/80">
                {t("auth.signup.haveAccount")}{" "}
                <Link to="/login" className="font-medium text-[#98dea3] hover:underline">
                  {t("auth.signup.login")}
                </Link>
              </p>
            </form>
          )}

          {/* ── PHONE FORM ── */}
          {tab === "phone" && !otpStep && (
            <form onSubmit={sendOtp} className="mt-8 space-y-5">
              {/* Full name */}
              <div>
                <label htmlFor="p_fullName" className="mb-2 block text-sm text-white/90">
                  {t("auth.signup.fullName")}
                </label>
                <input
                  id="p_fullName"
                  name="fullName"
                  type="text"
                  value={phoneForm.fullName}
                  onChange={onPhoneChange}
                  placeholder={t("auth.signup.fullName_ph")}
                  className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 text-white placeholder-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  required
                />
              </div>

              {/* Email for notifications */}
              <div>
                <label htmlFor="p_email" className="mb-2 block text-sm text-white/90">
                  {t("auth.phone.emailLabel")}
                </label>
                <input
                  id="p_email"
                  name="email"
                  type="email"
                  value={phoneForm.email}
                  onChange={onPhoneChange}
                  placeholder={t("auth.phone.emailPh")}
                  className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 text-white placeholder-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  required
                />
              </div>

              {/* Phone number */}
              <div>
                <label htmlFor="p_phone" className="mb-2 block text-sm text-white/90">
                  {t("auth.phone.phoneLabel")}
                </label>
                <input
                  id="p_phone"
                  name="phone"
                  type="tel"
                  value={phoneForm.phone}
                  onChange={onPhoneChange}
                  placeholder={t("auth.phone.phonePh")}
                  className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 text-white placeholder-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  required
                />
                <p className="mt-1 text-xs text-white/40">{t("auth.phone.phoneHint")}</p>
              </div>

              <RoleToggle
                role={phoneForm.role}
                setRole={(r) => setPhoneForm((f) => ({ ...f, role: r }))}
                t={t}
              />

              {phoneError && (
                <p className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                  {phoneError}
                </p>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={phoneLoading}
                  className="w-full rounded-xl bg-[#7269e3] px-5 py-3 text-lg font-semibold text-white shadow-[0_6px_20px_rgba(114,105,227,0.35)] hover:brightness-110 disabled:opacity-60"
                >
                  {phoneLoading ? t("auth.phone.sending") : t("auth.phone.sendOtp")}
                </button>
              </div>

              <p className="text-center text-white/80">
                {t("auth.signup.haveAccount")}{" "}
                <Link to="/login" className="font-medium text-[#98dea3] hover:underline">
                  {t("auth.signup.login")}
                </Link>
              </p>
            </form>
          )}

          {/* ── OTP STEP ── */}
          {tab === "phone" && otpStep && (
            <form onSubmit={verifyOtp} className="mt-8 space-y-5">
              <p className="text-center text-white/70">
                {t("auth.phone.otpSent", { phone: phoneForm.phone })}
              </p>

              <div>
                <label htmlFor="otp" className="mb-2 block text-sm text-white/90">
                  {t("auth.phone.otpLabel")}
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 text-center text-2xl tracking-[0.3em] text-white placeholder-white/40 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  required
                />
              </div>

              {phoneError && (
                <p className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                  {phoneError}
                </p>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={phoneLoading}
                  className="w-full rounded-xl bg-[#7269e3] px-5 py-3 text-lg font-semibold text-white shadow-[0_6px_20px_rgba(114,105,227,0.35)] hover:brightness-110 disabled:opacity-60"
                >
                  {phoneLoading ? t("auth.phone.verifying") : t("auth.phone.verify")}
                </button>
              </div>

              <button
                type="button"
                onClick={() => {
                  setOtpStep(false);
                  setOtp("");
                  setPhoneError("");
                }}
                className="w-full text-center text-sm text-white/50 hover:text-white/80"
              >
                {t("auth.phone.changeNumber")}
              </button>
            </form>
          )}

          {/* Invisible reCAPTCHA mount point */}
          <div id="recaptcha-container" ref={recaptchaContainerRef} />
        </section>
      </div>
    </main>
  );
}
